import React from 'react';
import { motion } from 'framer-motion';
import { 
    School, Building2, Landmark, GraduationCap, 
    BookOpen, Users, Globe, Zap, CheckCircle2,
    ArrowRight, LayoutDashboard, Database
} from 'lucide-react';
import PublicLayout from '../components/layout/PublicLayout';
import useSEO from '../hooks/useSEO';

const SolutionsPage = () => {
    useSEO({
        title: 'Academic Administration Solutions | ScholarsAnchor B2B AI SaaS',
        description: 'Explore customizable AI administration solutions for higher education, secondary schools, and training centers. ScholarsAnchor advances safe, legal B2B AI SaaS capabilities.',
        keywords: 'scholars anchor, B2B AI SaaS educational, AI for educational institutions, secondary school scheduling, higher education administrative AI, ScholarsAnchor solutions',
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "ScholarsAnchor Academic Solutions",
            "description": "Tailored academic administration and scheduling solutions for educational institutions, utilizing advanced B2B AI SaaS frameworks.",
            "publisher": {
                "@type": "Organization",
                "name": "Threnlabs",
                "url": "https://threnlabs.com"
            }
        }
    });

    const solutions = [
        {
            icon: Landmark,
            title: "Higher Education",
            description: "Scale-ready infrastructure for large universities handling thousands of faculty members and complex multi-campus scheduling.",
            features: ["Multi-campus synchronization", "Departmental autonomy controls", "Academic year forecasting"]
        },
        {
            icon: School,
            title: "Secondary Schools",
            description: "Simplified workflows for K-12 institutions to manage teacher rotations, room changes, and parental notifications.",
            features: ["Teacher substitution engine", "Room utilization heatmaps", "Automated parent reporting"]
        },
        {
            icon: Building2,
            title: "Professional Training",
            description: "Agile scheduling for corporate training centers and vocational schools with high turnover and dynamic course loads.",
            features: ["Inventory & resource tracking", "Credential management", "On-demand course generation"]
        }
    ];

    return (
        <PublicLayout>
            <div className="pt-40 pb-20 px-6">
                <div className="max-w-7xl mx-auto">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center mb-32"
                    >
                        <h1 className="text-5xl md:text-8xl font-black tracking-tighter mb-8 bg-gradient-to-b from-zinc-950 to-zinc-600 dark:from-white dark:to-white/40 bg-clip-text text-transparent">
                            Solutions for Every <br /> Academic Ambition.
                        </h1>
                        <p className="text-neutral-600 dark:text-neutral-400 text-xl md:text-2xl max-w-3xl mx-auto font-medium leading-relaxed">
                            Whether you're a boutique institute or a global university system, our B2B AI SaaS adapts to your unique administrative architecture, advancing secure and compliant AI for educational institutions.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-40">
                        {solutions.map((solution, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.1 }}
                                className="group relative p-12 rounded-[3rem] bg-white/50 dark:bg-zinc-900/50 border border-neutral-200 dark:border-white/5 hover:border-indigo-500/30 transition-all duration-500 shadow-sm dark:shadow-none"
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-[3rem]"></div>
                                <div className="w-20 h-20 rounded-3xl bg-indigo-500/10 flex items-center justify-center mb-10 border border-indigo-500/20 group-hover:scale-110 transition-transform duration-500">
                                    <solution.icon className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
                                </div>
                                <h3 className="text-3xl font-black text-zinc-900 dark:text-white mb-6">{solution.title}</h3>
                                <p className="text-neutral-600 dark:text-neutral-500 font-medium leading-relaxed mb-10">
                                    {solution.description}
                                </p>
                                <ul className="space-y-4 mb-12">
                                    {solution.features.map((feature, j) => (
                                        <li key={j} className="flex items-center gap-3 text-sm font-bold text-zinc-800 dark:text-zinc-300">
                                            <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                                <button className="w-full py-5 rounded-2xl bg-white dark:bg-white/5 border border-neutral-200 dark:border-white/10 text-zinc-800 dark:text-white font-black text-sm uppercase tracking-widest hover:bg-neutral-900 hover:text-white dark:hover:bg-white dark:hover:text-black transition-all">
                                    Case Study
                                </button>
                            </motion.div>
                        ))}
                    </div>

                    {/* Impact Section - Hidden temporarily
                    <div className="relative rounded-[4rem] bg-gradient-to-br from-indigo-900/20 to-blue-900/20 border border-white/5 p-12 md:p-32 overflow-hidden mb-40 shadow-2xl">
                        <div className="absolute top-0 right-0 p-20 opacity-5 -rotate-12">
                            <Globe className="w-[600px] h-[600px]" />
                        </div>
                        
                        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
                            <div>
                                <h2 className="text-4xl md:text-6xl font-black tracking-tighter text-white mb-8">
                                    Global Impact, <br /> Local Excellence.
                                </h2>
                                <p className="text-zinc-400 text-lg md:text-xl font-medium leading-relaxed mb-12">
                                    Our platform is trusted by institutions across 40+ countries to deliver flawless administrative experiences every single day.
                                </p>
                                <div className="grid grid-cols-2 gap-12">
                                    {[
                                        { val: "500+", label: "Institutions" },
                                        { val: "10M+", label: "Schedules" },
                                        { val: "99.9%", label: "Uptime" },
                                        { val: "24/7", label: "Support" }
                                    ].map((stat, i) => (
                                        <div key={i}>
                                            <div className="text-4xl font-black text-white mb-2">{stat.val}</div>
                                            <div className="text-xs font-bold text-indigo-400 uppercase tracking-widest">{stat.label}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="relative">
                                <div className="aspect-square rounded-[3rem] bg-white/5 border border-white/10 p-12 backdrop-blur-sm relative overflow-hidden group">
                                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.1)_0,transparent_70%)]"></div>
                                    <div className="relative h-full flex flex-col justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                                                <GraduationCap className="w-6 h-6 text-indigo-400" />
                                            </div>
                                            <div>
                                                <div className="text-white font-black">University of Excellence</div>
                                                <div className="text-xs font-bold text-zinc-500 uppercase tracking-widest">Customer since 2024</div>
                                            </div>
                                        </div>
                                        <p className="text-2xl font-medium text-zinc-300 italic leading-relaxed">
                                            "This system has reduced our administrative workload by 60%. The algorithm is simply unparalleled in the market."
                                        </p>
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-full bg-zinc-800 border border-white/10" />
                                            <div>
                                                <div className="text-sm font-black text-white uppercase tracking-tighter">Dr. Sarah Jenkins</div>
                                                <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Dean of Administration</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    */}
                </div>
            </div>
        </PublicLayout>
    );
};

export default SolutionsPage;
